<template>
  <div class="chart">
    <Config
      :question_result="question_result"
      :groupCode="groupCode"
      :defaultTheme="defaultTheme(question_result)"
      :question_result_index="question_result_index"
    />

    <div class="charts-types" v-if="!loaderChart">
      <div v-for="(chart, index) in arrayResults()" :key="index" class="matrix-chart">
        <h1
          v-if="resultTypeIsMatrix && chartType() !== CHART_TYPES.STACKED_BAR"
          class="title"
        >
          {{ chart.label }}
        </h1>

        <component
          :is="getCharsComponent(chartType())"
          :id="`${question_result.code}-${chartType()}`"
          :question_result="question_result"
          :colors="chartColors(theme, question_result)"
          :chartIndex="index"
          :isPercent="isPercent"
          :isMatrix="resultTypeIsMatrix"
          :question_result_index="question_result_index"
          :groupCode="groupCode"
        />

        <div v-if="chartType() !== CHART_TYPES.STACKED_BAR">
          <div class="table-wrapper">
            <div class="header">
              <div>{{ parseTranslation(OPTION) }}</div>
              <div>{{ parseTranslation(FREQUENCY) }}</div>
              <div>{{ parseTranslation(COUNT) }}</div>
            </div>

            <div class="table-scroll">
              <div
                v-for="(item, itemIndex) in arrayQuestionResults(index)"
                :key="itemIndex"
                class="item"
              >
                <div>{{ item.label }}</div>

                <div>
                  <p v-if="chartType() == CHART_TYPES.WORD_CLOUD">
                    {{ getPercentage(item.count, arrayQuestionResults(index)) }}%
                  </p>

                  <p>{{ item.percentage }}</p>

                  <p>{{ item.count }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="footer">
            <div>{{ parseTranslation(TOTAL) }}:</div>
            <div>{{ totalCount(arrayQuestionResults(index)) }}</div>
          </div>
        </div>

        <div v-else class="table-stacked-bar">
          <div class="table-stacked-bar-scroll">
            <div class="table-stacked-bar-header">
              <div>{{ parseTranslation(OPTION) }}</div>
              <div
                v-for="(result, resultIndex) in arrayQuestionResults(index)"
                :key="resultIndex"
              >
                {{ result.label }}
              </div>
            </div>

            <div class="table-stacked-bar-body">
              <div
                v-for="(result, resultIndex) in arrayResultsTableStackedBar()"
                :key="resultIndex"
              >
                <div>
                  {{ resultTypeIsMatrix ? result.label : 'N/A' }}
                </div>

                <div
                  v-for="(item, itemIndex) in arrayValuesTableStackedBar(result)"
                  :key="itemIndex"
                >
                  <p>{{ isPercent ? item.percentage : item.count }}</p>
                </div>
              </div>
            </div>

            <div class="footer-table-stacked-bar">
              <div>
                {{ parseTranslation(TOTAL) }}: <span>{{ question_result.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="loader">
      <Loader :color="'#0695fd'" :size="50" />
    </div>
  </div>
</template>

<script>
import Loader from '@/components/loading/Loader-3'

import Config from './configs'

import BarChart from './ChartTypes/BarChart'
import HorizontalBarChart from './ChartTypes/HorizontalBarChart'
import PieChart from './ChartTypes/PieChart'
import LineChart from './ChartTypes/LineChart'
import StackedBarChart from './ChartTypes/StackedBarChart'
import WordCloud from './ChartTypes/WordCloud'

import { CHART_TYPES } from './chartConfig'
import { chartColors, defaultTheme } from '../utils/chartThemes'

import { RESULT_TYPES } from '../questions.constants'

import * as translationChart from './charts.translations'

const { default: translations, ...translationKeys } = translationChart

export default {
  name: 'Chart',

  components: {
    Config,
    BarChart,
    HorizontalBarChart,
    PieChart,
    LineChart,
    StackedBarChart,
    WordCloud,
    Loader,
  },

  props: {
    question_result: {
      required: true,
      type: Object,
    },

    groupCode: {
      required: true,
    },

    question_result_index: {
      required: true,
    },
  },

  data() {
    return {
      loaderChart: false,
      CHART_TYPES,
      RESULT_TYPES,

      translations,
      ...translationKeys,
    }
  },

  computed: {
    resultTypeIsMatrix() {
      return this.question_result.resultType === this.RESULT_TYPES.MATRIX
    },

    isPercent() {
      return this.question_result?.chartSettings?.isPercent || false
    },

    theme() {
      return this.question_result?.chartSettings?.theme
    },

    resultsLength() {
      return this.question_result?.results.length
    },
  },

  watch: {
    question_result() {
      this.loaderChart = true

      setTimeout(() => {
        this.loaderChart = false
      }, 1)
    },
  },

  methods: {
    getCharsComponent() {
      switch (this.chartType()) {
        case CHART_TYPES.BAR:
          return 'BarChart'
        case CHART_TYPES.BAR_HORIZONTAL:
          return 'HorizontalBarChart'
        case CHART_TYPES.PIE:
          return 'PieChart'
        case CHART_TYPES.LINE:
          return 'LineChart'
        case CHART_TYPES.STACKED_BAR:
          return 'StackedBarChart'
        case CHART_TYPES.WORD_CLOUD:
          return 'WordCloud'
        default:
          throw new Error(`${this.chartType()} not a valid chart type`)
      }
    },

    chartType() {
      const wordCloud = CHART_TYPES.WORD_CLOUD

      const chart = this.question_result?.chartSettings?.type || CHART_TYPES.BAR

      const isWordCloud = this.question_result.resultType === CHART_TYPES.WORD_CLOUD

      return isWordCloud ? wordCloud : chart
    },

    chartColors,

    defaultTheme,

    arrayResults() {
      const chartTypeIsNotStackedBar = this.chartType() !== CHART_TYPES.STACKED_BAR

      const results = this.question_result.results

      const arrayResults =
        this.resultTypeIsMatrix && chartTypeIsNotStackedBar ? results : [results[0]]

      return arrayResults
    },

    arrayQuestionResults(index) {
      const results = this.question_result.results

      if (this.resultTypeIsMatrix) {
        return results[index].subAnswers
      }

      return results
    },

    arrayResultsTableStackedBar() {
      const results = this.question_result.results

      if (this.resultTypeIsMatrix) return results

      return [results[0]]
    },

    arrayValuesTableStackedBar(result) {
      if (this.resultTypeIsMatrix) return result.subAnswers

      return this.question_result.results
    },

    totalCount(results) {
      return results.reduce((acc, result) => acc + result.count, 0)
    },

    getPercentage(value, results) {
      const percentage = (value * 100) / this.totalCount(results)

      return percentage.toFixed(2)
    },
  },
}
</script>

<style lang="scss" scoped>
.chart {
  .charts-types {
    margin-top: 30px;

    .matrix-chart {
      margin-bottom: 70px;

      &:nth-last-of-type(1) {
        margin-bottom: 0;
      }

      .title {
        @include tipography(18);
        text-align: center;
        margin: 20px 0;
        overflow-wrap: break-word;
        line-height: 1.25rem;

        .light & {
          color: $gray-2;
        }
      }
    }

    .table-wrapper {
      margin-top: 60px;
      margin-bottom: 20px;

      .header {
        display: flex;
        margin-bottom: 10px;

        > div {
          padding: 10px;
          width: 10%;
          background: $gray-8;
          @include tipography(16);
          font-weight: bold;
          border-radius: 3px;

          &:nth-of-type(1) {
            margin-right: 5px;
            width: 80%;
          }

          &:nth-of-type(3) {
            text-align: end;
          }

          .light & {
            color: $gray-2;
            background: $gray-light-10;
          }
        }

        @media (max-width: 767.98px) {
          > div {
            width: 25%;

            &:nth-of-type(1) {
              width: 50%;
            }
          }
        }
      }

      .table-scroll {
        max-height: 400px;
        overflow-x: auto;
        overflow-y: scroll;

        &::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          height: 10px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .item {
          display: flex;

          &:last-of-type {
            > div {
              border-bottom: none;
            }
          }

          > div {
            width: calc(20% - 13px);
            padding: 10px;
            @include tipography(16);
            border-bottom: 1px solid $gray-light-10;

            &:nth-of-type(1) {
              width: calc(80% + 8px);
            }

            &:nth-of-type(2) {
              display: flex;
              justify-content: space-between;

              p {
                margin-bottom: 0;
                padding: 0 5px;
              }
            }

            .light & {
              color: $gray-2;
            }
          }

          @media (max-width: 767.98px) {
            > div {
              width: calc(50% - 13px);

              &:nth-of-type(1) {
                width: calc(50% + 8px);
              }
            }
          }
        }
      }
    }

    .footer {
      width: calc(20% - 5px);
      margin-top: 10px;
      margin-left: auto;
      padding: 10px 15px;
      border-radius: 3px;
      @include tipography(16);
      font-weight: bold;
      display: flex;
      background: $gray-8;

      div {
        width: 50%;

        &:nth-last-of-type(1) {
          text-align: end;
          font-weight: normal;
        }
      }

      .light & {
        color: $gray-2;
        background: $gray-light-10;
      }

      @media (max-width: 767.98px) {
        width: calc(50% - 5px);
      }
    }
  }
}

.table-stacked-bar {
  margin: 30px 0 20px;

  .table-stacked-bar-scroll {
    overflow: auto;
    max-height: 400px;
    padding-bottom: 10px;
    position: relative;

    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 10px;
      height: 10px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }

  .table-stacked-bar-header {
    display: flex;

    div {
      padding: 10px;
      margin-right: 10px;
      min-width: 130px;
      flex: 1;
      font-weight: bold;
      @include tipography(16);
      background: $gray-8;
      border-radius: 3px;

      .light & {
        color: $gray-2;
        background: $gray-light-10;
      }
    }
  }

  .table-stacked-bar-body {
    > div {
      @include flexAlignCenter;
      border-bottom: 1px solid $gray-light-10;

      &:last-of-type {
        border-bottom: none;
      }

      > div {
        padding: 10px;
        @include flexAlignCenter;
        flex: 1;
        min-width: 140px;
        @include tipography(16);

        p {
          margin-bottom: 0;
        }

        .light & {
          color: $gray-2;
        }
      }
    }
  }

  .footer-table-stacked-bar {
    width: 100%;
    display: flex;
    padding: 10px;
    margin-top: 16px;
    background: $gray-8;
    border-radius: 3px;

    .light & {
      background: $gray-light-10;
    }

    div {
      flex: 1;
      min-width: 130px;
      font-weight: bold;
      color: $white;

      .light & {
        color: $gray-2;
      }

      span {
        margin-left: 8px;
      }

      &:nth-of-type(1) {
        min-width: 270px;
      }
    }
  }
}

.loader {
  @include flexMiddle;
  height: 400px;
}
</style>
